import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiUsers, FiStar, FiBook, FiMail, FiTrendingUp, 
  FiTrendingDown, FiClock, FiEye, FiEdit 
} from 'react-icons/fi';
import { getDashboardStats } from '../../utils/adminApi';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getDashboardStats();
        setStats(data.stats);
        setRecentActivity(data.recent_activity);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Total Consultations',
      value: stats?.consultations?.total || 0,
      subtitle: `${stats?.consultations?.pending || 0} pending`,
      icon: FiUsers,
      color: 'bg-blue-500',
      link: '/admin/consultations'
    },
    {
      title: 'Transformations',
      value: stats?.transformations?.total || 0,
      subtitle: `${stats?.transformations?.featured || 0} featured`,
      icon: FiStar,
      color: 'bg-green-500',
      link: '/admin/transformations'
    },
    {
      title: 'Recipes',
      value: stats?.recipes?.total || 0,
      subtitle: `${stats?.recipes?.featured || 0} featured`,
      icon: FiBook,
      color: 'bg-orange-500',
      link: '/admin/recipes'
    },
    {
      title: 'Contact Messages',
      value: stats?.contacts?.total || 0,
      subtitle: `${stats?.contacts?.unread || 0} unread`,
      icon: FiMail,
      color: 'bg-purple-500',
      link: '/admin/contacts'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-8 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Dashboard Overview</h2>
        <p className="text-gray-600">Welcome to the Dfitclub admin panel. Here's what's happening today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Link to={card.link} className="block">
              <div className="card p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{card.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                    <p className="text-sm text-gray-500">{card.subtitle}</p>
                  </div>
                  <div className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center`}>
                    <card.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Consultations */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="card"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Consultations</h3>
              <Link to="/admin/consultations" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentActivity?.consultations?.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.consultations.map((consultation) => (
                  <div key={consultation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{consultation.name}</p>
                      <p className="text-sm text-gray-600">{consultation.email}</p>
                      <p className="text-xs text-gray-500">
                        Goal: {consultation.goal === 'weight_loss' ? 'Weight Loss' : 'Weight Gain'}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        consultation.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : consultation.status === 'contacted'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {consultation.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(consultation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent consultations</p>
            )}
          </div>
        </motion.div>

        {/* Recent Contacts */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="card"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Messages</h3>
              <Link to="/admin/contacts" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentActivity?.contacts?.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.contacts.map((contact) => (
                  <div key={contact.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{contact.name}</p>
                      <p className="text-sm text-gray-600">{contact.subject}</p>
                      <p className="text-xs text-gray-500 truncate max-w-xs">{contact.message}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        contact.status === 'new' 
                          ? 'bg-red-100 text-red-800' 
                          : contact.status === 'read'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {contact.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent messages</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/transformations"
            className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
              <FiStar className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Add Transformation</p>
              <p className="text-sm text-gray-600">Share a success story</p>
            </div>
          </Link>

          <Link
            to="/admin/recipes"
            className="flex items-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
          >
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
              <FiBook className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Add Recipe</p>
              <p className="text-sm text-gray-600">Create a new healthy recipe</p>
            </div>
          </Link>

          <Link
            to="/admin/consultations"
            className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
              <FiUsers className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Review Consultations</p>
              <p className="text-sm text-gray-600">Manage client requests</p>
            </div>
          </Link>
        </div>
      </motion.div>

    </div>
  );
};

export default AdminDashboard;